import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Users, 
  CalendarDays, 
  BarChart3, 
  Menu, 
  X,
  Stethoscope,
  User,
  LogOut,
  Settings,
  ChevronDown,
  Briefcase,
  Activity,
  Package
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import MiniCalendar from './MiniCalendar';
import Logo from './Logo';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  // Navigation based on user role
  const getNavigation = () => {
    if (user?.role === 'patient') {
      return [
        { name: 'Mis Citas', href: '/appointments', icon: Activity },
      ];
    }
    
    // Navigation for staff (admin, dentist, assistant, receptionist)
    return [
      { name: 'Dashboard', href: '/dashboard', icon: BarChart3 },
      { name: 'Calendario', href: `/calendar?date=${new Date().toISOString().split('T')[0]}&view=timeGridWeek`, icon: CalendarDays },
      { name: 'Pacientes', href: '/patients', icon: Users },
      { name: 'Citas', href: '/appointments', icon: Activity },
      { name: 'Productos', href: '/products', icon: Package },
      { name: 'Administración', href: '/administration', icon: Briefcase },
    ];
  };

  const navigation = getNavigation();

  const isActive = (path: string) => {
    // For patients, default to appointments page
    if (user?.role === 'patient') {
      if (path === '/appointments') {
        return location.pathname === '/appointments' || location.pathname === '/' || location.pathname === '/dashboard';
      }
    } else {
      // For staff, normal behavior
      if (path === '/dashboard') {
        return location.pathname === '/dashboard' || location.pathname === '/';
      }
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-48 flex-col bg-white">
          <div className="flex h-16 items-center justify-between px-4">
            <Logo size="md" variant="dark" />
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 px-4 py-4">
            <ul className="space-y-2">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                        isActive(item.href)
                          ? 'bg-dental-100 text-dental-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <Icon className="mr-3 h-5 w-5" />
                      {item.name}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
          
          {/* Mini Calendar for mobile */}
          <div className="px-4 py-2">
            <MiniCalendar />
          </div>
          
          {/* User section at bottom of mobile sidebar */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center gap-x-3">
              <div className="h-8 w-8 rounded-full bg-dental-100 flex items-center justify-center">
                {user?.avatar ? (
                  <img src={user.avatar} alt={user.firstName} className="h-8 w-8 rounded-full" />
                ) : (
                  <span className="text-sm font-medium text-dental-600">
                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                  </span>
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
            </div>
            
            <div className="mt-3 space-y-1">
              <button
                onClick={() => setSidebarOpen(false)}
                className="flex w-full items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
              >
                <User className="mr-3 h-4 w-4" />
                Mi Perfil
              </button>
              
              {user?.role === 'admin' && (
                <Link
                  to="/settings"
                  onClick={() => setSidebarOpen(false)}
                  className="flex w-full items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  <Settings className="mr-3 h-4 w-4" />
                  Configuración
                </Link>
              )}
              
              <hr className="my-2" />
              
              <button
                onClick={() => {
                  setSidebarOpen(false);
                  logout();
                }}
                className="flex w-full items-center px-3 py-2 text-sm text-red-700 hover:bg-red-50 rounded-md"
              >
                <LogOut className="mr-3 h-4 w-4" />
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop sidebar - always visible */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-48 lg:flex-col">
        <div className="flex flex-col h-full bg-white border-r border-gray-200">
          {/* Header */}
          <div className="flex h-16 items-center px-3 bg-gradient-to-r from-dental-500 to-dental-600 flex-shrink-0">
            <Logo size="md" variant="light" />
          </div>
          
          {/* Scrollable content area */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* Navigation */}
            <nav className="px-3 py-4 flex-shrink-0">
              <ul className="space-y-2">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className={`flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                          isActive(item.href)
                            ? 'bg-dental-100 text-dental-700'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        <Icon className="mr-2 h-4 w-4" />
                        {item.name}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>
            
            {/* Mini Calendar - compact and scrollable if needed */}
            <div className="px-3 py-1 flex-1 overflow-y-auto min-h-0">
              <div className="max-h-64 overflow-hidden">
                <MiniCalendar />
              </div>
            </div>
          </div>
          
          {/* User section - always visible at bottom */}
          <div className="border-t border-gray-200 p-2 flex-shrink-0">
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex w-full items-center gap-x-2 rounded-md p-1.5 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              >
                <div className="h-6 w-6 rounded-full bg-dental-100 flex items-center justify-center flex-shrink-0">
                  {user?.avatar ? (
                    <img src={user.avatar} alt={user.firstName} className="h-6 w-6 rounded-full" />
                  ) : (
                    <span className="text-xs font-medium text-dental-600">
                      {user?.firstName?.[0]}{user?.lastName?.[0]}
                    </span>
                  )}
                </div>
                <div className="flex-1 text-left truncate min-w-0">
                  <p className="text-xs font-medium text-gray-900 truncate">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-gray-500 capitalize truncate">{user?.role}</p>
                </div>
                <ChevronDown className="h-3 w-3 flex-shrink-0" />
              </button>

              {userMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <div className="absolute bottom-full left-0 z-20 mb-2 w-full rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                    <div className="px-3 py-2 border-b border-gray-200">
                      <p className="text-xs font-medium text-gray-900">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                    </div>
                    
                    <Link
                      to="/profile"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex w-full items-center px-3 py-2 text-xs text-gray-700 hover:bg-gray-100"
                    >
                      <User className="mr-2 h-3 w-3" />
                      Mi Perfil
                    </Link>
                    
                    {user?.role === 'admin' && (
                      <Link
                        to="/settings"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex w-full items-center px-3 py-2 text-xs text-gray-700 hover:bg-gray-100"
                      >
                        <Settings className="mr-2 h-3 w-3" />
                        Configuración
                      </Link>
                    )}
                    
                    <hr className="my-1" />
                    
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        logout();
                      }}
                      className="flex w-full items-center px-3 py-2 text-xs text-red-700 hover:bg-red-50"
                    >
                      <LogOut className="mr-2 h-3 w-3" />
                      Cerrar Sesión
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main content - always with left padding for sidebar */}
      <div className="lg:pl-48 h-screen flex flex-col">
        {/* Top bar for mobile */}
        <div className="lg:hidden">
          <div className="flex h-12 shrink-0 items-center border-b border-gray-200 bg-white px-4">
            <button
              type="button"
              className="-m-2.5 p-2.5 text-gray-700"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Page content */}
        <main className={location.pathname.includes('/calendar') ? 'p-0 flex-1 overflow-hidden' : 'p-2 lg:p-4 flex-1'}>
          <div className={location.pathname.includes('/calendar') ? 'w-full h-full' : 'w-full'}>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;















